import { Field, Form, Formik } from 'formik'
import React from 'react'
import InputField from '../form/fields/input'
import { useCreateMutation, useUpdateMutation } from '../../queries/job-titles'
import { JobTitle } from '../../api/job-titles'
import SelectField from '../form/fields/select'
import { buildListAsInputOptions } from '../../utils/forms'
import { RequestButton } from '../request-components/request-components'
import TextareaField from '../form/fields/textarea'
import { useClockingRulesQuery } from '../../queries/clocking-rules'
import ClockingRulesDescription from '../clocking-rules/clocking-rules-description'
import { Box, Card, Element, Heading, Icon, Message } from 'react-bulma-components'
import FormField from '../form/fields/form-field'
import { isEqual } from 'lodash'
import { WarningIcon } from '../icons'
import { compareDocsById } from '../../utils/documents'

interface JobTitleFormProps {
  jobTitle?: JobTitle
}

const JobTitleForm: React.FC<JobTitleFormProps> = ({ jobTitle }) => {
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateMutation()
  const clockingRulesQuery = useClockingRulesQuery()

  const currentClockingRule =
    jobTitle && clockingRulesQuery.data?.find(c => compareDocsById(c._id, jobTitle?.clockingRule))
  console.log({ ...jobTitle, interimAgencySettings: undefined })
  return (
    <Formik
      initialValues={
        jobTitle
          ? {
              ...jobTitle,
              interimAgencySettings: undefined,
              clockingRule: jobTitle.clockingRule?._id,
            }
          : {
              name: '',
              description: '',
              complementaryInformations: '',
              clockingRule: '',
            }
      }
      enableReinitialize
      validate={values => {
        const errors: any = {}
        if (!values.name) errors.name = 'Champ requis'
        if (!values.clockingRule) errors.clockingRule = 'Champ requis'
        return errors
      }}
      onSubmit={values => {
        if (jobTitle) {
          updateMutation.mutate({ ...values, id: jobTitle._id.toString() })
        } else {
          createMutation.mutate(values)
        }
      }}
    >
      {({ values }) => {
        const clockingRules =
          values.clockingRule &&
          clockingRulesQuery.data?.find &&
          clockingRulesQuery.data.find(clockingRules => clockingRules._id === values.clockingRule)

        return (
          <Form autoComplete="off">
            <Heading size={4} mb={5}>
              Informations de base
            </Heading>
            <Field label="Nom " name="name" component={InputField} required />
            <Field name="description" component={TextareaField} label="Description interne" />
            <hr />
            <FormField
              label="Informations complémentaires pour les Travailleurs"
              help="Ces informations seront systématiquement transmises aux Travailleurs recevant une mission pour ce Métier"
            >
              <Field name="complementaryInformations" component={TextareaField} />
            </FormField>
            <Element mb={5} mt={6}>
              <Heading size={5} mb={5}>
                Coefficients de main d'oeuvre
              </Heading>
              <Message color="info">
                <Message.Body>
                  Les coefficients de main d'oeuvre sont gérés individuellement pour chaque agence
                  d'intérim.
                  <br />
                  Vous pouvez les configurer depuis la section "
                  <a href="/settings/labor-costs-coefficients">Coefficients Agences d'Intérim</a>".
                </Message.Body>
              </Message>
            </Element>
            <Element mb={5} mt={6}>
              <Heading size={5} mb={5}>
                Règles de pointage
              </Heading>
              <Field
                name="clockingRule"
                component={SelectField}
                options={
                  clockingRulesQuery.data ? buildListAsInputOptions(clockingRulesQuery.data) : []
                }
              />
              {clockingRules && (
                <>
                  <Card mb={3}>
                    <Card.Content>
                      <Element textWeight="bold" mb={3}>
                        Règle à l'Entrée
                      </Element>
                      <ClockingRulesDescription clockingRules={clockingRules} type="start" />
                    </Card.Content>
                  </Card>
                  <Card mb={3}>
                    <Card.Content>
                      <Element textWeight="bold" mb={3}>
                        Règle à la Sortie
                      </Element>
                      <ClockingRulesDescription clockingRules={clockingRules} type="end" />
                    </Card.Content>
                  </Card>
                </>
              )}
            </Element>
            {jobTitle &&
              clockingRules &&
              currentClockingRule &&
              !isEqual(values.clockingRule, jobTitle.clockingRule) && (
                <>
                  <Heading size={4} mt={5}>
                    <Icon.Text>
                      <Icon mr={1}>
                        <WarningIcon color="red" />
                      </Icon>
                      Modification des journées de travail en cours{' '}
                    </Icon.Text>
                  </Heading>
                  <Box>
                    <Message color="warning" p={2}>
                      Vous avez modifié la règle de pointage du métier{' '}
                      <Element renderAs="strong">{jobTitle.name}</Element> (anciennement{' '}
                      <Element renderAs="strong">{currentClockingRule.name}</Element>).
                      <br />
                      <br />
                      En sélectionnant une date, la nouvelle règle de pointage (
                      <Element renderAs="strong">{clockingRules.name}</Element>) sera appliquée à
                      toutes les journées de travail:
                      <Element className="content">
                        <ul>
                          <li>Non validées</li>
                          <li>Commençant à partir de la date sélectionnée</li>
                        </ul>
                      </Element>
                      <Element>
                        Sans date sélectionnée, la nouvelle règle ne s'appliquera qu'aux futures
                        missions. Les missions et journées de travail existantes conserveront
                        l'ancienne règle.
                      </Element>
                    </Message>

                    <Field
                      name="updateExistingWorkPeriodsFrom"
                      component={InputField}
                      type="date"
                      label="Modifier les journées de travail à partir du"
                      help="Si aucune date n'est choisie, aucune journée de travail ne sera modifiée"
                    />
                  </Box>
                </>
              )}
            <RequestButton color="primary" type="submit" mutation={updateMutation} mt={6}>
              {jobTitle ? 'Mettre à jour le Métier' : 'Nouveau Métier'}
            </RequestButton>
          </Form>
        )
      }}
    </Formik>
  )
}
export default JobTitleForm
